<template>
  <div v-if="layer" class="attributes-container" :style="{ height: sheetHeight + 'px' }">
    <div class="resize-handle" title="Arrastrar para cambiar altura" @pointerdown="startResize"></div>
    <div class="header-bar">
      <span class="layer-title">{{ layer.name }}</span>
      <span v-if="recordSummaryText" class="record-summary">{{ recordSummaryText }}</span>
      <div class="header-tools">
        <button class="icon-btn" :disabled="isBusy" :class="{ active: appliedFilter.mode === 'quick' }" title="Aplicar filtros por columna" @click="applyQuickFilters"><font-awesome-icon icon="search" /></button>
        <button class="icon-btn" :disabled="isBusy" :class="{ active: appliedFilter.mode === 'advanced' }" title="Filtros avanzados" @click="openAdvancedPanel"><font-awesome-icon icon="filter" /></button>
        <button class="icon-btn" title="Filtros guardados" @click="openSavedFiltersModal"><font-awesome-icon icon="bookmark" /></button>
        <button v-if="hasActiveTableFilter" type="button" class="filters-clear-btn" :disabled="isBusy" title="Limpiar filtros" @click="clearAllFilters">Limpiar filtros</button>
        <button v-if="isTerrainLayer" class="icon-btn" :disabled="isBusy" title="Actualizar" @click="refresh"><font-awesome-icon icon="sync-alt" /></button>
        <button class="icon-btn" :disabled="isBusy" title="Descargar datos" @click="openDownloadModal"><font-awesome-icon icon="download" /></button>
        <button class="icon-btn" :class="{ active: columnsConfigOpen }" :disabled="isBusy" title="Configurar columnas" @click="openColumnsConfig"><font-awesome-icon icon="cogs" /></button>
        <DownloadModal />
        <SavedFiltersModal @applied="zoomToSavedFilter" />
        <button class="icon-btn" :disabled="!hasAppliedRoi || isBusy" :class="{ active: hasAppliedRoi }" title="Limpiar filtro espacial" @click="clearViewportFilter"><font-awesome-icon icon="times-circle" /></button>
        <button class="icon-btn" :disabled="firstPage || isBusy" title="Anterior" @click="prevPage"><font-awesome-icon icon="chevron-left" /></button>
        <button class="icon-btn" :disabled="lastPage || isBusy" title="Siguiente" @click="nextPage"><font-awesome-icon icon="chevron-right" /></button>
        <button class="icon-btn" :title="isMinimized ? 'Restaurar' : 'Minimizar'" @click="toggleMinimized"><font-awesome-icon :icon="isMinimized ? 'plus' : 'minus'" /></button>
        <button class="icon-btn close-btn" title="Cerrar" @click="closeTable"><font-awesome-icon icon="times" /></button>
      </div>
    </div>

    <div v-if="isLoading" class="filter-progress-banner" role="status">
      <span class="loading-spinner small"></span>
      <span class="filter-progress-message">
        {{ pendingMessage }}
        <template v-if="pendingElapsedMs >= 3000">({{ Math.floor(pendingElapsedMs / 1000) }}s)</template>
      </span>
      <button type="button" class="cancel-filter-btn" @click="cancelPendingFilter">Cancelar</button>
    </div>

    <modal name="ColumnsConfigModal" :width="480" height="auto" :adaptive="true" :scrollable="true" class="columns-config-vmodal"
      @opened="columnsConfigOpen = true" @closed="columnsConfigOpen = false">
      <div class="columns-config-modal" aria-labelledby="columns-config-title">
        <div class="columns-config-header">
          <strong id="columns-config-title">Configurar columnas</strong>
          <button type="button" title="Cerrar" @click="$modal.hide('ColumnsConfigModal')"><font-awesome-icon icon="times" /></button>
        </div>
        <p>Arrastre una columna, use los botones para reordenarla o desmárquela para ocultarla.</p>
        <input v-model="columnSearch" type="text" class="columns-search-input" placeholder="Buscar columna..." />
        <label class="columns-select-all">
          <input type="checkbox" :checked="allColumnsVisible" :indeterminate.prop="someColumnsVisible" @change="toggleAllColumns($event.target.checked)">
          <span>Seleccionar todas</span>
        </label>
        <ul class="columns-config-list">
          <li v-for="header in filteredConfigHeaders" :key="`config-${header}`" :class="{ dragging: draggingColumn === header, hidden: isColumnHidden(header) }"
            draggable="true" @dragstart="startColumnDrag(header, $event)" @dragover.prevent @drop="dropColumn(header)" @dragend="draggingColumn = null">
            <span class="column-drag-handle" title="Arrastrar"><font-awesome-icon icon="sort" /></span>
            <span class="column-config-name" :title="headerAlias(header)">{{ headerAlias(header) }}</span>
            <label class="column-visibility-control" :title="isColumnHidden(header) ? 'Mostrar columna' : 'Ocultar columna'">
              <input type="checkbox" :checked="!isColumnHidden(header)" @change="toggleColumnVisibility(header)">
              <span class="sr-only">Mostrar {{ headerAlias(header) }}</span>
            </label>
            <button type="button" :disabled="orderedHeaders.indexOf(header) === 0" title="Subir columna" @click="moveColumn(header, -1)"><font-awesome-icon icon="arrow-up" /></button>
            <button type="button" :disabled="orderedHeaders.indexOf(header) === orderedHeaders.length - 1" title="Bajar columna" @click="moveColumn(header, 1)"><font-awesome-icon icon="arrow-down" /></button>
          </li>
        </ul>
        <div class="columns-config-footer">
          <button class="reset-columns-btn" type="button" @click="resetColumnOrder">Restablecer</button>
          <button class="apply-columns-btn" type="button" @click="$modal.hide('ColumnsConfigModal')">Listo</button>
        </div>
      </div>
    </modal>

    <div v-show="!isMinimized" class="table-wrapper">
      <div v-if="initialLoading" class="initial-loading" role="status"><span class="loading-spinner"></span><span>Cargando atributos…</span></div>
      <table class="attr-table">
        <colgroup><col v-for="header in headers" :key="header"></colgroup>
        <thead>
          <tr><th v-for="header in headers" :key="`h-${header}`" class="col-header" :class="{ dragging: draggingColumn === header }"
            :title="`${headerAlias(header)} — arrastre para reordenar`" draggable="true"
            @dragstart="startColumnDrag(header, $event)" @dragover.prevent @drop="dropColumn(header)" @dragend="draggingColumn = null">{{ headerAlias(header) }}</th></tr>
          <tr>
            <th v-for="header in headers" :key="`f-${header}`" class="filter-cell">
              <span v-if="isAttachmentHeader(header)" class="attachment-filter-placeholder">Adjuntos</span>
              <AttributeFilter v-else :field-type="fieldType(header)" :filter="filterFor(header)" :error="filterErrors[header] || ''"
                :field="header" :distinct-values-fetcher="distinctValuesFetcher" :auto-apply="true"
                @change="onFilterChange(header, $event)" @apply="applyQuickFilters" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in displayAttributes" :key="index" class="clickable-row" :class="{ odd: index % 2, selected: selectedRow === index }" title="Clic para centrar y destacar en el mapa" @click="zoomToFeature(row, index, $event)">
            <td v-for="header in headers" :key="`d-${header}`" class="data-cell" :class="{ 'data-cell--attachment': isTerrainAttachmentValue(row[header]) }" :title="cellTitle(header, row[header])">
              <span v-if="isTerrainAttachmentValue(row[header])" @click.stop>
                <AttachmentGallery
                  compact
                  :attachment-ids="row[header].attachmentIds"
                  :attachment-names="row[header].attachmentNames"
                  :kind="row[header].kind"
                  :label="row[header].label"
                />
              </span>
              <span v-else v-html="cellHtml(header, row[header])"></span>
            </td>
          </tr>
          <tr v-if="!displayAttributes.length"><td :colspan="Math.max(headers.length, 1)" class="no-data">Sin datos disponibles</td></tr>
        </tbody>
      </table>
    </div>

    <AdvancedFilterPanel
      v-if="advancedPanelOpen"
      :draft="advancedDraft"
      :schema="advancedFieldSchema"
      :fields="availableFilterFields"
      :aliases="fieldAliases"
      :source-layer="layer"
      :minimized="advancedPanelMinimized"
      :busy="isBusy"
      :bottom-offset="sheetHeight"
      @change="setAdvancedDraft"
      @apply="applyAdvancedFilter"
      @clear="clearAdvancedFilter"
      @close="closeAdvancedPanel"
      @toggle-minimized="toggleAdvancedMinimized"
      @save="saveAdvancedFilter"
    />
  </div>
</template>

<script>
import { attributesTableService, isTerrainLayer, stripPropertiesPublicPrefix } from "@/services/layers/attributesTable";
import { filterActivityApi } from "@/services/layers/filterActivityApi";
import { makeDistinctValuesFetcher } from "@/services/layers/distinctValues";
import { configuredColumns, visibleConfiguredFields } from "@/services/layers/fieldsConfig";
import { sanitizeHtml, stripHtml } from "@/services/helpers/displayValueHelper";
import { mapService } from "@/services/map/mapService";
import { attributeSelectionService } from "@/services/map/attributeSelectionService";
import { advancedRoiService } from "@/services/map/advancedRoiService";
import { targetFeatureSelectionService } from "@/services/map/targetFeatureSelectionService";
import { computeAggregateBbox, padExtent } from "@/services/helpers/bboxHelper";
import {
  FILTER_VERSION,
  emptyAdvancedDraft,
  emptyAppliedFilter,
  cloneFilter,
  hasAttributePredicate,
  hasAppliedPredicate,
  isConditionActive,
  quickAppliedFilter,
} from "@/services/layers/advancedFilters";
import DownloadModal from "./DownloadModal.vue";
import AttributeFilter from "./AttributeFilter.vue";
import AdvancedFilterPanel from "./AdvancedFilterPanel.vue";
import SavedFiltersModal from "./SavedFiltersModal.vue";
import AttachmentGallery from "@/components/terrain/AttachmentGallery.vue";
import {
  createTerrainAttachmentValue,
  isTerrainAttachmentValue,
  parseTerrainAttachmentIds,
} from "@/services/map/selectService";

const TERRAIN_ATTACHMENT_HEADER_PREFIX = "__terrain_attachment__:";
const ATTACHMENT_IDS_SUFFIX = ".attachmentIds";
const ATTACHMENT_NAMES_SEGMENT = ".attachmentNames.";

export default {
  components: { DownloadModal, AttributeFilter, AdvancedFilterPanel, SavedFiltersModal, AttachmentGallery },
  data() {
    return {
      offset: 0,
      isLoading: false, selectedRow: null,
      filterErrors: {},
      sheetHeight: 280,
      lastExpandedHeight: 280,
      isMinimized: false,
      resizing: false,
      resizeStartY: 0,
      resizeStartHeight: 280,
      activeRequest: null,
      pendingIsCrossLayer: false,
      pendingKnownComplex: null,
      pendingElapsedMs: 0,
      pendingTimer: null,
      lastMapWarning: null,
      columnOrder: [],
      draggingColumn: null,
      columnsConfigOpen: false,
      hiddenColumns: [],
      columnSearch: '',
    };
  },
  computed: {
    layer() { return this.$store.getters["attributeTable/layer"]; },
    pendingSavedFiltersModal() { return this.$store.getters["attributeTable/pendingSavedFiltersModal"]; },
    // Multiselect buscable del operador "in" (AttributeFilter.vue) — ver
    // services/layers/distinctValues.js. AdvancedFilterPanel arma el suyo
    // propio a partir de :source-layer (ya recibido acá abajo), así que
    // esto solo hace falta para la fila de filtro rápido de esta tabla.
    distinctValuesFetcher() { return makeDistinctValuesFetcher(this.layer); },
    attributes() { return this.$store.getters["attributeTable/attributes"]; },
    terrainAttachmentGroups() {
      if (!this.isTerrainLayer) return [];
      const fields = new Set(Object.keys(this.fieldSchema));
      this.attributes.forEach(row => Object.keys(row).forEach(field => fields.add(field)));
      const byFieldId = new Map();
      const ensure = fieldId => {
        if (!byFieldId.has(fieldId)) {
          const terrainField = (this.layer.provider.terrainFields || []).find(field => field.fieldId === fieldId);
          byFieldId.set(fieldId, {
            fieldId,
            header: `${TERRAIN_ATTACHMENT_HEADER_PREFIX}${fieldId}`,
            label: (terrainField && terrainField.label) || fieldId,
            idsField: null,
            nameFields: {},
            rawFields: [],
          });
        }
        return byFieldId.get(fieldId);
      };
      fields.forEach(field => {
        const stripped = stripPropertiesPublicPrefix(field);
        if (stripped.endsWith(ATTACHMENT_IDS_SUFFIX)) {
          const fieldId = stripped.slice(0, -ATTACHMENT_IDS_SUFFIX.length);
          const group = ensure(fieldId);
          group.idsField = field;
          group.rawFields.push(field);
          return;
        }
        const namesIndex = stripped.indexOf(ATTACHMENT_NAMES_SEGMENT);
        if (namesIndex < 0) return;
        const fieldId = stripped.slice(0, namesIndex);
        const attachmentId = stripped.slice(namesIndex + ATTACHMENT_NAMES_SEGMENT.length);
        const group = ensure(fieldId);
        group.nameFields[attachmentId] = field;
        group.rawFields.push(field);
      });
      return Array.from(byFieldId.values());
    },
    terrainAttachmentRawFields() {
      return new Set(this.terrainAttachmentGroups.flatMap(group => group.rawFields));
    },
    displayAttributes() {
      if (!this.terrainAttachmentGroups.length) return this.attributes;
      return this.attributes.map(row => {
        const display = {};
        Object.entries(row).forEach(([field, value]) => {
          if (!this.terrainAttachmentRawFields.has(field)) display[field] = value;
        });
        this.terrainAttachmentGroups.forEach(group => {
          const ids = parseTerrainAttachmentIds(row[group.idsField]);
          const names = {};
          Object.entries(group.nameFields).forEach(([attachmentId, field]) => {
            if (row[field] != null) names[attachmentId] = String(row[field]);
          });
          display[group.header] = ids.length ? createTerrainAttachmentValue(ids, names, group.label) : null;
        });
        return display;
      });
    },
    totalFeatures() { return this.$store.getters["attributeTable/totalFeatures"]; },
    // totalUnfiltered llega aparte (fetchTotalCount, fire-and-forget) y puede
    // no estar listo todavía cuando la tabla recién abre — null lo distingue
    // de "0 registros" real.
    totalUnfiltered() { return this.$store.getters["attributeTable/totalUnfiltered"]; },
    recordSummaryText() {
      if (!this.metadata) return "";
      if (this.metadata.empty) return "Sin registros";
      const totalText = this.totalFeatures.toLocaleString("es-CL");
      if (!hasAppliedPredicate(this.appliedFilter)) return `${totalText} registro${this.totalFeatures === 1 ? "" : "s"}`;
      if (this.totalUnfiltered === null) return `${totalText} cumplen el filtro`;
      return `${totalText} de ${this.totalUnfiltered.toLocaleString("es-CL")} registros cumplen el filtro`;
    },
    columnFilters() { return this.$store.getters["attributeTable/columnFilters"] || {}; },
    fieldSchema() { return this.$store.getters["attributeTable/fieldSchema"] || {}; },
    fieldAliases() { return this.$store.getters["attributeTable/fieldAliases"] || {}; },
    advancedFieldSchema() {
      const schema = { ...this.fieldSchema };
      Object.keys(this.fieldAliases).forEach(field => { if (!schema[field]) schema[field] = "unknown"; });
      if (this.attributes.length) {
        Object.keys(this.attributes[0]).forEach(field => {
          if (!["bbox", "geometry"].includes(field) && !schema[field]) schema[field] = "unknown";
        });
      }
      return schema;
    },
    availableFilterFields() {
      return Array.from(new Set([
        ...this.headers,
        ...Object.keys(this.fieldSchema),
        ...Object.keys(this.fieldAliases),
      ])).filter(field => !["bbox", "geometry"].includes(field)
        && this.fieldSchema[field] !== "geometry"
        && !this.terrainAttachmentRawFields.has(field)
        && !this.isAttachmentHeader(field));
    },
    metadata() { return this.$store.getters["attributeTable/metadata"]; },
    appliedFilter() { return this.$store.getters["attributeTable/appliedFilter"] || emptyAppliedFilter(); },
    advancedDraft() { return this.$store.getters["attributeTable/advancedDraft"] || emptyAdvancedDraft(); },
    advancedPanelOpen() { return this.$store.getters["attributeTable/advancedPanelOpen"]; },
    advancedPanelMinimized() { return this.$store.getters["attributeTable/advancedPanelMinimized"]; },
    initialLoading() { return this.$store.getters["attributeTable/loading"]; },
    isBusy() { return this.isLoading || this.initialLoading; },
    // pendingMessage: si ya sabemos que la capa objetivo es liviana
    // (estimateCrossLayerTargetComplexity, capas importadas — ya están en
    // memoria, no hace falta adivinar) no mostramos la advertencia. Si
    // sabemos que es pesada, la mostramos de inmediato. Si no lo sabemos
    // (GeoServer/ArcGIS — la geometría recién se pide en este momento),
    // esperamos ~3s de espera real antes de asumir que puede ser un caso
    // lento — así no se alarma al usuario en filtros rápidos por defecto.
    pendingMessage() {
      if (!this.pendingIsCrossLayer) return "Aplicando filtro…";
      const knownHeavy = this.pendingKnownComplex && this.pendingKnownComplex.complex;
      const knownLight = this.pendingKnownComplex && !this.pendingKnownComplex.complex;
      if (knownLight) return "Cruzando con la capa objetivo…";
      if (knownHeavy || this.pendingElapsedMs >= 3000) {
        return "Cruzando con la capa objetivo — con capas de geometría compleja puede tardar hasta un minuto, no se está trabando.";
      }
      return "Cruzando con la capa objetivo…";
    },
    // Solo los campos activos de la configuración de Campos de la capa, en su
    // orden — mismo criterio que el popup (configuredColumns hace fallback a
    // todas las columnas si la capa no tiene campos visibles configurados).
    availableHeaders() {
      const keys = this.displayAttributes.length
        ? Object.keys(this.displayAttributes[0]).filter(key => !["bbox", "geometry"].includes(key))
        : [
          ...Object.keys(this.fieldSchema).filter(key => this.fieldSchema[key] !== "geometry" && !this.terrainAttachmentRawFields.has(key)),
          ...this.terrainAttachmentGroups.map(group => group.header),
        ];
      return configuredColumns(keys, this.layer && this.layer.campos).map(column => column.name);
    },
    configuredAliases() {
      return Object.fromEntries(visibleConfiguredFields(this.layer && this.layer.campos)
        .filter(campo => campo.alias)
        .map(campo => [campo.name, campo.alias]));
    },
    orderedHeaders() {
      const available = this.availableHeaders;
      const ordered = this.columnOrder.filter(field => available.includes(field));
      return [...ordered, ...available.filter(field => !ordered.includes(field))];
    },
    headers() { return this.orderedHeaders.filter(field => !this.hiddenColumns.includes(field)); },
    // #77: buscador del modal Configurar columnas (solo filtra el listado visible)
    filteredConfigHeaders() {
      const query = this.columnSearch.trim().toLowerCase();
      if (!query) return this.orderedHeaders;
      return this.orderedHeaders.filter(header =>
        this.headerAlias(header).toLowerCase().includes(query) || header.toLowerCase().includes(query));
    },
    // #75: cualquier filtro activo sobre la tabla (aplicado o escrito en la fila rápida)
    hasActiveTableFilter() {
      return hasAppliedPredicate(this.appliedFilter) || Object.values(this.columnFilters).some(isConditionActive);
    },
    allColumnsVisible() { return this.orderedHeaders.length > 0 && this.headers.length === this.orderedHeaders.length; },
    someColumnsVisible() { return this.headers.length > 0 && !this.allColumnsVisible; },
    firstPage() { return this.offset === 0; },
    lastPage() { return this.totalFeatures ? this.offset + 10 >= this.totalFeatures : this.attributes.length < 10; },
    hasAppliedRoi() {
      return !!(this.appliedFilter.mode === "advanced" && this.appliedFilter.advanced.roi && this.appliedFilter.advanced.roi.enabled && this.appliedFilter.advanced.roi.spec);
    },
    // "Actualizar" manual: por ahora solo en Capa_Terreno (única capa con
    // datos que cambian mientras la tabla está abierta — nuevas respuestas
    // aprobadas), ver conversación sobre reactividad de la tabla.
    isTerrainLayer() { return isTerrainLayer(this.layer); },
  },
  watch: {
    initialLoading(loading) { if (!loading && this.layer && this.metadata) this.syncMapFilter(false); },
    layer: {
      immediate: true,
      handler(layer, previousLayer) {
        this.loadColumnOrder(layer);
        if (this.columnsConfigOpen) this.$modal.hide("ColumnsConfigModal");
        this.columnsConfigOpen = false;
        const changedLayer = layer && (!previousLayer || layer.id !== previousLayer.id);
        if (changedLayer && this.isMinimized) {
          this.isMinimized = false;
          this.$nextTick(() => this.setSheetHeight(Math.max(180, this.lastExpandedHeight || 280)));
        }
        // Sólo para el caso "tabla ya montada, cambia de capa" — el primer
        // montaje (previousLayer undefined) lo cubre mounted(): este watcher
        // immediate corre en created(), antes de que SavedFiltersModal (hijo)
        // termine de montarse y suscribirse a $modal, así que $modal.show
        // acá sería un no-op silencioso.
        if (changedLayer && previousLayer) this.maybeOpenPendingSavedFiltersModal();
      },
    },
    // Cubre el caso "tabla ya abierta en ESTA MISMA capa" — clickManageSavedFilters
    // (ListPlegable.vue) reutiliza el layer que ya está en el store, así que ni el
    // watcher de `layer` (mismo id, changedLayer=false) ni mounted() (componente ya
    // montado) disparan. Acá sí cambia el flag pendingSavedFiltersModal, así que basta
    // reaccionar a eso directamente.
    pendingSavedFiltersModal(pending) { if (pending) this.maybeOpenPendingSavedFiltersModal(); },
    availableHeaders(headers) {
      if (!headers.length) return;
      this.columnOrder = [
        ...this.columnOrder.filter(field => headers.includes(field)),
        ...headers.filter(field => !this.columnOrder.includes(field)),
      ];
      this.hiddenColumns = this.hiddenColumns.filter(field => headers.includes(field));
    },
    metadata(metadata) { this.applyDefaultColumnPrefs(metadata); },
  },
  mounted() {
    this.setSheetHeight(280);
    window.addEventListener("attribute-filter-state-change", this.onExternalFilterStateChange);
    window.addEventListener("attribute-table-selection-clear", this.clearSelectedRow);
    // Primer montaje de la tabla (tabla recién abierta): acá sí está
    // garantizado que SavedFiltersModal (hijo) ya se montó y se suscribió a
    // $modal, a diferencia del watcher `layer` con immediate:true.
    this.maybeOpenPendingSavedFiltersModal();
  },
  beforeDestroy() {
    window.removeEventListener("attribute-filter-state-change", this.onExternalFilterStateChange);
    window.removeEventListener("attribute-table-selection-clear", this.clearSelectedRow);
    this.$modal.hide("ColumnsConfigModal");
    this.removeResizeListeners();
    if (this.activeRequest) this.activeRequest.abort();
    this.stopPendingTimer();
    advancedRoiService.detach();
  },
  methods: {
    isTerrainAttachmentValue,
    isAttachmentHeader(header) { return header.startsWith(TERRAIN_ATTACHMENT_HEADER_PREFIX); },
    attachmentGroup(header) { return this.terrainAttachmentGroups.find(group => group.header === header); },
    cellTitle(header, value) {
      return isTerrainAttachmentValue(value) ? `${value.attachmentIds.length} adjunto(s)` : stripHtml(this.formattedCellValue(header, value));
    },
    cellHtml(header, value) {
      return sanitizeHtml(this.formattedCellValue(header, value));
    },
    formattedCellValue(header, value) {
      return attributesTableService.formatAttributeValue(this.metadata, header, value);
    },
    // startPendingTimer/stopPendingTimer alimentan el banner no bloqueante de
    // filter-progress-banner: cuenta segundos transcurridos y distingue el
    // caso cross-layer (potencialmente lento, ver PB-24_v4) del resto de
    // filtros (casi instantáneos) para no mostrar el mensaje de "puede
    // tardar" cuando no aplica. pendingKnownComplex usa
    // estimateCrossLayerTargetComplexity (capas importadas, ya en memoria)
    // para saber la complejidad real de antemano en vez de adivinar — ver
    // computed pendingMessage.
    startPendingTimer(candidate) {
      const crossLayer = candidate.mode === "advanced" && candidate.advanced.crossLayer;
      this.pendingIsCrossLayer = !!(crossLayer && crossLayer.enabled);
      this.pendingKnownComplex = this.pendingIsCrossLayer
        ? attributesTableService.estimateCrossLayerTargetComplexity(crossLayer.targetLayerId)
        : null;
      this.pendingElapsedMs = 0;
      const startedAt = Date.now();
      this.pendingTimer = setInterval(() => { this.pendingElapsedMs = Date.now() - startedAt; }, 500);
    },
    stopPendingTimer() {
      if (this.pendingTimer) { clearInterval(this.pendingTimer); this.pendingTimer = null; }
      this.pendingIsCrossLayer = false;
      this.pendingKnownComplex = null;
      this.pendingElapsedMs = 0;
    },
    cancelPendingFilter() {
      if (this.activeRequest) this.activeRequest.abort();
    },
    onExternalFilterStateChange(event) {
      if (!this.layer || !event.detail || event.detail.layerId !== this.layer.id) return;
      this.offset = 0;
      if (event.detail.enabled === false) { advancedRoiService.clear(); targetFeatureSelectionService.clear(); }
    },
    openColumnsConfig() {
      if (this.columnsConfigOpen) { this.$modal.hide("ColumnsConfigModal"); return; }
      this.columnSearch = '';
      if (this.isMinimized) {
        this.isMinimized = false;
        this.setSheetHeight(Math.max(180, this.lastExpandedHeight || 280));
      }
      this.$nextTick(() => this.$modal.show("ColumnsConfigModal"));
    },
    columnOrderKey(layer = this.layer) { return layer ? `attrTable_columns_${layer.id}` : null; },
    hiddenColumnsKey(layer = this.layer) { return layer ? `attrTable_hidden_columns_${layer.id}` : null; },
    loadColumnOrder(layer) {
      const key = this.columnOrderKey(layer);
      const hiddenKey = this.hiddenColumnsKey(layer);
      if (!key) { this.columnOrder = []; this.hiddenColumns = []; return; }
      try {
        const saved = JSON.parse(localStorage.getItem(key) || "[]");
        this.columnOrder = Array.isArray(saved) ? saved : [];
      } catch (error) { this.columnOrder = []; }
      try {
        const hidden = JSON.parse(localStorage.getItem(hiddenKey) || "[]");
        this.hiddenColumns = Array.isArray(hidden) ? hidden : [];
      } catch (error) { this.hiddenColumns = []; }
    },
    // applyDefaultColumnPrefs: la primera vez que se abre la tabla de una
    // capa con `metadata.defaultHiddenFields` (hoy solo Capa_Terreno, ver
    // attributesTable.js::getGeoServerMetadata) y sin preferencia guardada
    // todavía, oculta esos campos técnicos y los manda al final del orden.
    // Si el usuario ya guardó una preferencia (aunque sea "restablecer"),
    // esto no se vuelve a aplicar — se respeta lo que eligió.
    applyDefaultColumnPrefs(metadata) {
      const technical = metadata && Array.isArray(metadata.defaultHiddenFields) ? metadata.defaultHiddenFields : [];
      if (!technical.length) return;
      const orderKey = this.columnOrderKey();
      const hiddenKey = this.hiddenColumnsKey();
      if (!orderKey || localStorage.getItem(orderKey) !== null || localStorage.getItem(hiddenKey) !== null) return;
      const available = this.availableHeaders;
      const technicalPresent = technical.filter(field => available.includes(field));
      if (!technicalPresent.length) return;
      this.columnOrder = [...available.filter(field => !technicalPresent.includes(field)), ...technicalPresent];
      this.hiddenColumns = technicalPresent;
    },
    saveColumnOrder() {
      const key = this.columnOrderKey();
      if (!key) return;
      try {
        localStorage.setItem(key, JSON.stringify(this.orderedHeaders));
        localStorage.setItem(this.hiddenColumnsKey(), JSON.stringify(this.hiddenColumns));
      } catch (error) { /* storage unavailable */ }
    },
    startColumnDrag(header, event) {
      this.draggingColumn = header;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", header);
    },
    dropColumn(target) {
      const source = this.draggingColumn;
      if (!source || source === target) { this.draggingColumn = null; return; }
      const order = this.orderedHeaders.slice();
      const sourceIndex = order.indexOf(source);
      order.splice(sourceIndex, 1);
      order.splice(order.indexOf(target), 0, source);
      this.columnOrder = order;
      this.draggingColumn = null;
      this.saveColumnOrder();
    },
    moveColumn(header, direction) {
      const order = this.orderedHeaders.slice();
      const index = order.indexOf(header);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= order.length) return;
      order.splice(index, 1);
      order.splice(target, 0, header);
      this.columnOrder = order;
      this.saveColumnOrder();
    },
    resetColumnOrder() {
      this.columnOrder = this.availableHeaders.slice();
      this.hiddenColumns = [];
      this.saveColumnOrder();
    },
    isColumnHidden(header) { return this.hiddenColumns.includes(header); },
    toggleColumnVisibility(header) {
      if (this.isColumnHidden(header)) this.hiddenColumns = this.hiddenColumns.filter(field => field !== header);
      else this.hiddenColumns = [...this.hiddenColumns, header];
      this.saveColumnOrder();
    },
    toggleAllColumns(visible) {
      this.hiddenColumns = visible ? [] : this.orderedHeaders.slice();
      this.saveColumnOrder();
    },
    headerAlias(field) {
      const attachment = this.attachmentGroup(field);
      return attachment ? attachment.label : (this.configuredAliases[field] || this.fieldAliases[field] || field);
    },
    fieldType(field) { return this.isAttachmentHeader(field) ? "attachment" : (this.fieldSchema[field] || "unknown"); },
    filterFor(field) {
      const existing = this.columnFilters[field];
      if (existing && existing.operator) return existing;
      // "En uno de" (el multiselect buscable) es el método principal de
      // búsqueda que se pidió — arranca ahí en vez de "Contiene"; el
      // usuario sigue pudiendo cambiar de operador desde el mismo selector.
      return { operator: "in", value: [] };
    },
    onFilterChange(field, filter) { this.$delete(this.filterErrors, field); this.$store.dispatch("attributeTable/setColumnFilter", { key: field, filter }); },
    async applyCandidate(candidate, expectedMode, { zoom = true } = {}) {
      if (this.isBusy || !this.layer || !this.metadata) return false;
      const previous = this.appliedFilter;
      if (previous.mode !== "none" && previous.mode !== expectedMode && hasAppliedPredicate(previous)) {
        if (!window.confirm("El nuevo modo reemplazará el filtro aplicado actualmente. ¿Desea continuar?")) return false;
      }
      if (this.activeRequest) this.activeRequest.abort();
      attributesTableService.invalidateCrossLayer(this.layer, candidate);
      const controller = new AbortController(); this.activeRequest = controller; this.isLoading = true;
      this.startPendingTimer(candidate);
      try {
        const result = await attributesTableService.fetchAttributes(this.layer, { offset: 0, appliedFilter: candidate, metadata: this.metadata, signal: controller.signal, mapId: this.$route.params.idMap });
        if (this.activeRequest !== controller) return false;
        await this.$store.dispatch("attributeTable/updateAttributes", { attributes: result.features, totalFeatures: result.total });
        await this.$store.dispatch("attributeTable/commitAppliedFilter", candidate);
        // Auditoría: se registra sólo acá, después de que el filtro se aplicó
        // con éxito — applyCandidate es el embudo único de los modos quick y
        // advanced, y lo dispara un clic explícito, no cada tecleo.
        filterActivityApi.record({
          layerId: this.layer.id,
          layerName: this.layer.name,
          mapId: this.$route.params.idMap,
          mode: candidate.mode,
          filter: candidate,
        });
        this.offset = 0;
        this.selectedRow = null;
        attributeSelectionService.clear();
        this.syncMapFilter(true);
        if (zoom) await this.zoomToResults();
        return true;
      } catch (error) {
        if (error.name !== "AbortError") this.$store.dispatch("toast/show", { message: error.message || "No se pudo aplicar el filtro", type: "error" });
        return false;
      } finally {
        if (this.activeRequest === controller) { this.activeRequest = null; this.isLoading = false; }
        this.stopPendingTimer();
      }
    },
    async applyQuickFilters() {
      this.filterErrors = attributesTableService.validateFilters(this.columnFilters, this.fieldSchema);
      if (Object.keys(this.filterErrors).length) return;
      const active = Object.values(this.columnFilters).some(isConditionActive);
      await this.applyCandidate(active ? quickAppliedFilter(this.columnFilters) : emptyAppliedFilter(), "quick");
    },
    async applyAdvancedFilter(draft) {
      const candidate = { version: FILTER_VERSION, mode: "advanced", advanced: draft };
      if (await this.applyCandidate(candidate, "advanced")) this.$store.dispatch("attributeTable/setAdvancedDraft", draft);
    },
    async clearAdvancedFilter() {
      if (this.appliedFilter.mode === "advanced") {
        if (!await this.applyCandidate(emptyAppliedFilter(), "advanced", { zoom: false })) return;
      }
      this.$store.dispatch("attributeTable/setAdvancedDraft", emptyAdvancedDraft());
      advancedRoiService.clear();
      targetFeatureSelectionService.clear();
    },
    // #75: vuelve la tabla al estado sin filtros (rápidos, avanzados o llegados
    // del menú de filtros avanzados) reutilizando las vías existentes
    async clearAllFilters() {
      if (this.appliedFilter.mode === "quick") {
        // QA-003: aplicar primero; si falla, los inputs conservan sus valores
        // (el orden no afecta al candidato: emptyAppliedFilter no lee columnFilters)
        if (!await this.applyCandidate(emptyAppliedFilter(), "quick", { zoom: false })) return;
        this.$store.dispatch("attributeTable/clearColumnFilters");
        return;
      }
      this.$store.dispatch("attributeTable/clearColumnFilters");
      await this.clearAdvancedFilter();
    },
    async clearViewportFilter() {
      if (!this.hasAppliedRoi) return;
      const draft = cloneFilter(this.appliedFilter.advanced);
      draft.roi.spec = null;
      const candidate = draft.attribute.enabled && hasAttributePredicate(draft.attribute.root)
        ? { version: FILTER_VERSION, mode: "advanced", advanced: draft }
        : emptyAppliedFilter();
      if (await this.applyCandidate(candidate, "advanced", { zoom: false })) {
        this.$store.dispatch("attributeTable/setAdvancedDraft", draft);
        advancedRoiService.clear();
      }
    },
    syncMapFilter(showWarning) {
      if (this.metadata?.provider === "upload") {
        attributesTableService.syncImportedMapFilter(this.layer, this.appliedFilter, this.metadata);
        return;
      }
      const params = attributesTableService.buildMapFilterParams(this.layer, this.appliedFilter, this.metadata);
      mapService.updateLayerFilter(this.layer.id, params);
      const roi = this.appliedFilter.mode === "advanced" && this.appliedFilter.advanced.roi && this.appliedFilter.advanced.roi.enabled
        ? this.appliedFilter.advanced.roi.spec : null;
      if (roi) { advancedRoiService.init(mapService.get()); advancedRoiService.setSpec(roi, false); }
      else if (!this.advancedPanelOpen) advancedRoiService.clear();
      if (showWarning && params.mapWarning && params.mapWarning !== this.lastMapWarning) {
        this.lastMapWarning = params.mapWarning;
        this.$store.dispatch("toast/show", { message: params.mapWarning, type: "warning" });
      }
    },
    async page(offset) {
      if (this.isBusy) return;
      this.isLoading = true;
      try {
        await attributesTableService.updateTable(this.layer, { offset, appliedFilter: this.appliedFilter, metadata: this.metadata, mapId: this.$route.params.idMap });
        this.offset = offset;
        // La fila seleccionada pertenece a la página anterior: el índice ya
        // no corresponde a la misma feature en la página nueva, así que el
        // resaltado (selected: selectedRow === index) quedaría marcando una
        // fila distinta si no se limpia acá.
        this.selectedRow = null;
        attributeSelectionService.clear();
      } catch (error) { this.$store.dispatch("toast/show", { message: error.message || "No se pudo cambiar de página", type: "error" }); }
      finally { this.isLoading = false; }
    },
    // "Actualizar" manual (Capa_Terreno): re-pide el metadata con force=true
    // porque una campaña sin respuestas aprobadas queda cacheada como
    // metadata.empty (ver getGeoServerMetadata) — sin refrescarlo, nunca se
    // enteraría de que GeoServer ya provisionó el feature type con la
    // primera respuesta recién aprobada. Mantiene la página/filtro actual
    // en vez de resetear a la primera página como hace openTable().
    async refresh() {
      if (this.isBusy) return;
      this.isLoading = true;
      try {
        const metadata = await attributesTableService.getLayerMetadata(this.layer, true);
        await this.$store.dispatch("attributeTable/setFieldMetadata", { schema: metadata.schema, aliases: metadata.aliases, metadata });
        if (metadata.empty) {
          await this.$store.dispatch("attributeTable/updateAttributes", { attributes: [], totalFeatures: 0 });
          await this.$store.dispatch("attributeTable/setTotalUnfiltered", 0);
        } else {
          await attributesTableService.updateTable(this.layer, { offset: this.offset, appliedFilter: this.appliedFilter, metadata, mapId: this.$route.params.idMap });
          attributesTableService.refreshTotalUnfiltered(this.layer, metadata);
        }
      } catch (error) { this.$store.dispatch("toast/show", { message: error.message || "No se pudo actualizar la tabla", type: "error" }); }
      finally { this.isLoading = false; }
    },
    prevPage() { if (!this.firstPage) this.page(Math.max(0, this.offset - 10)); },
    nextPage() { if (!this.lastPage) this.page(this.offset + 10); },
    // Zoom a resultados necesita el bbox agregado de TODAS las coincidencias
    // del filtro, no solo de la página cargada en la tabla (this.attributes
    // está limitado a RECORDS_PER_PAGE) — si no, el zoom queda acotado a las
    // primeras filas y puede dejar fuera al resto (ej. capas con estados que
    // abarcan todo el continente).
    async zoomToResults() {
      let features = this.attributes;
      if (this.totalFeatures > this.attributes.length) {
        const result = await attributesTableService.fetchFeaturesForExtent(this.layer, this.appliedFilter, this.metadata);
        features = result.features;
        if (result.total > features.length) {
          this.$store.dispatch("toast/show", { message: `El zoom solo considera los primeros ${features.length} de ${result.total} registros`, type: "warning" });
        }
      }
      const { extent, missingCount, totalCount } = computeAggregateBbox(features);
      if (extent) {
        mapService.zoomToExtent(padExtent(extent, { paddingFactor: .1, minBuffer: 200 }));
        if (missingCount) this.$store.dispatch("toast/show", { message: `${missingCount} de ${totalCount} registros sin ubicación`, type: "warning" });
      } else this.$store.dispatch("toast/show", { message: features.length ? "Los resultados no tienen ubicación disponible" : "No se encontraron registros", type: "warning" });
    },
    // El zoom tras cargar un filtro guardado corre como handler de evento, fuera
    // del try de applyCandidate: si la consulta de extent falla hay que avisar
    // acá, o el error queda como un rechazo sin manejar y sin toast (QA-175).
    async zoomToSavedFilter() {
      try {
        await this.zoomToResults();
      } catch (error) {
        this.$store.dispatch("toast/show", { message: error.message || "No se pudo hacer zoom al filtro aplicado", type: "error" });
      }
    },
    openAdvancedPanel() {
      if (this.appliedFilter.mode === "quick" && hasAppliedPredicate(this.appliedFilter)) {
        // Modal genérico de vue-js-modal (dialog:true, <v-dialog/> en App.vue)
        // en vez de window.confirm — mismo patrón que
        // UploadShpKmz.vue::confirmDeleteFile.
        this.$modal.show("dialog", {
          title: "Abrir modo avanzado",
          text: "Abrir el modo avanzado conservará el filtro rápido hasta que aplique uno nuevo. ¿Desea continuar?",
          buttons: [
            { title: "Cancelar" },
            {
              title: "Continuar",
              class: "vue-dialog-button attributes-table-confirm-button",
              handler: () => {
                this.$modal.hide("dialog");
                this.$store.dispatch("attributeTable/setAdvancedPanel", true);
              },
            },
          ],
        });
        return;
      }
      this.$store.dispatch("attributeTable/setAdvancedPanel", true);
    },
    closeAdvancedPanel() { this.$store.dispatch("attributeTable/setAdvancedPanel", false); this.syncMapFilter(false); },
    toggleAdvancedMinimized() { this.$store.dispatch("attributeTable/setAdvancedPanelMinimized", !this.advancedPanelMinimized); },
    setAdvancedDraft(draft) { this.$store.dispatch("attributeTable/setAdvancedDraft", draft); },
    openSavedFiltersModal() { this.$modal.show("SavedFiltersModal"); },
    // pendingSavedFiltersModal: seteado por ListPlegable.vue::clickManageSavedFilters
    // antes de abrir la tabla de una capa — se consume acá (al montar la
    // tabla la primera vez y al cambiar de capa con la tabla ya abierta, ver
    // el watcher de `layer`) para abrir el modal de filtros guardados de una
    // vez, sin depender de un evento entre componentes hermanos.
    maybeOpenPendingSavedFiltersModal() {
      if (!this.$store.getters["attributeTable/pendingSavedFiltersModal"]) return;
      this.$store.dispatch("attributeTable/setPendingSavedFiltersModal", false);
      this.$nextTick(() => this.$modal.show("SavedFiltersModal"));
    },
    // "Guardar…" del panel avanzado: aplica el borrador igual que "Filtrar"
    // y, si se aplicó con éxito, abre el modal de filtros guardados para
    // nombrarlo — una sola implementación de guardado cubre rápido y avanzado.
    async saveAdvancedFilter(draft) {
      if (await this.applyCandidate({ version: FILTER_VERSION, mode: "advanced", advanced: draft }, "advanced")) {
        this.$store.dispatch("attributeTable/setAdvancedDraft", draft);
        this.$nextTick(() => this.$modal.show("SavedFiltersModal"));
      }
    },
    closeTable() { this.removeResizeListeners(); advancedRoiService.detach(); targetFeatureSelectionService.detach(); attributeSelectionService.clear(); this.$store.dispatch("attributeTable/closeTable"); },
    clearSelectedRow() { this.selectedRow = null; },
    // Clic en un enlace del valor (HTML interpretado) solo abre el enlace.
    zoomToFeature(row, index, event) { if (event && event.target.closest("a")) return; this.selectedRow = index; attributeSelectionService.highlight(this.layer, row, this.metadata); if (row.bbox) mapService.zoomToExtent(padExtent(row.bbox, { paddingFactor: .1, minBuffer: 200 })); },
    openDownloadModal() { this.$modal.show("DownloadModal"); },
    setSheetHeight(height) { this.sheetHeight = Math.round(height); this.$store.dispatch("attributeTable/setLayoutHeight", this.sheetHeight); },
    collapsedHeight() { const header = this.$el && this.$el.querySelector(".header-bar"); return header ? Math.ceil(header.getBoundingClientRect().height) : 52; },
    toggleMinimized() {
      if (this.isMinimized) { this.isMinimized = false; this.setSheetHeight(this.lastExpandedHeight); }
      else { if (this.columnsConfigOpen) this.$modal.hide("ColumnsConfigModal"); this.columnsConfigOpen = false; this.lastExpandedHeight = this.sheetHeight; this.isMinimized = true; this.setSheetHeight(this.collapsedHeight()); }
    },
    startResize(event) {
      this.resizing = true; this.resizeStartY = event.clientY; this.resizeStartHeight = this.sheetHeight;
      event.currentTarget.setPointerCapture(event.pointerId);
      window.addEventListener("pointermove", this.onResize); window.addEventListener("pointerup", this.stopResize);
    },
    onResize(event) {
      if (!this.resizing) return;
      const raw = this.resizeStartHeight + this.resizeStartY - event.clientY; const collapsed = this.collapsedHeight();
      if (raw <= collapsed + 68) { this.isMinimized = true; this.setSheetHeight(collapsed); return; }
      this.isMinimized = false; const height = Math.min(Math.max(180, window.innerHeight * .7), Math.max(180, raw));
      this.lastExpandedHeight = height; this.setSheetHeight(height);
    },
    stopResize() { this.resizing = false; this.removeResizeListeners(); },
    removeResizeListeners() { window.removeEventListener("pointermove", this.onResize); window.removeEventListener("pointerup", this.stopResize); },
  },
};
</script>

<style scoped>
.attributes-container { position:absolute; bottom:0; left:0; display:flex; flex-direction:column; overflow:hidden; width:100%; background:#fff; border:0; border-radius:0; box-shadow:none; font-family:'Inter','Segoe UI',sans-serif; z-index:1000; }
.resize-handle { position:absolute; top:0; left:0; width:100%; height:8px; cursor:ns-resize; touch-action:none; z-index:3; }
.resize-handle::after { content:""; position:absolute; top:3px; left:50%; width:44px; height:3px; transform:translateX(-50%); border-radius:2px; background:rgba(255,255,255,.65); }
.header-bar { flex:0 0 auto; display:grid; grid-template-columns:1fr auto 1fr; align-items:center; gap:12px; padding:12px 20px; background:var(--color-navy); border-bottom:1px solid rgba(255,255,255,.1); }
.layer-title { min-width:0; overflow:hidden; color:#fff; font-size:1.05rem; font-weight:600; text-overflow:ellipsis; white-space:nowrap; justify-self:start; }
.record-summary { justify-self:center; overflow:hidden; background:none; color:var(--color-line); font-size:1rem; font-weight:600; text-overflow:ellipsis; white-space:nowrap; }
.header-tools { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:7px; justify-self:end; }
.icon-btn { min-width:38px; min-height:32px; padding:6px 10px; border:0; border-radius:5px; background:var(--color-orange); color:#fff; cursor:pointer; }
.icon-btn.active { background:#22a060; box-shadow:inset 0 0 0 2px rgba(255,255,255,.35); }
.icon-btn:disabled { opacity:.45; cursor:not-allowed; }.close-btn{background:#c53b38}
.columns-config-modal { display:flex; flex-direction:column; max-height:80vh; overflow:hidden; padding:16px; background:#fff; }
.columns-config-header { display:flex; align-items:center; justify-content:space-between; gap:12px; color:var(--color-navy); }
.columns-config-header button { display:flex; align-items:center; justify-content:center; width:28px; height:28px; border:0; border-radius:4px; background:transparent; color:#64748b; cursor:pointer; }
.columns-config-modal p { margin:5px 0 10px; color:#64748b; font-size:.72rem; }
.columns-search-input { width:100%; box-sizing:border-box; margin:0 0 8px; padding:7px 10px; border:1px solid #cbd5e1; border-radius:5px; font-size:.78rem; }
/* El .filters-clear-btn global es para fondos claros; en la barra navy necesita contraste */
.filters-clear-btn { padding:6px 10px; border:1px solid rgba(255,255,255,.4); border-radius:5px; background:transparent; color:#fff; font-size:.75rem; font-weight:600; cursor:pointer; }
.filters-clear-btn:hover { background:rgba(255,255,255,.14); border-color:#fff; color:#fff; }
.filters-clear-btn:disabled { opacity:.45; cursor:not-allowed; }
.columns-select-all{display:flex;align-items:center;gap:8px;margin:0 0 8px;padding:8px 10px;border:1px solid #cbd5e1;border-radius:5px;background:#f8fafc;color:#334155;font-size:.78rem;font-weight:700;cursor:pointer}.columns-select-all input{width:16px;height:16px;margin:0;accent-color:var(--color-navy);cursor:pointer}
.columns-config-list { display:flex; flex:1; flex-direction:column; gap:5px; min-height:80px; max-height:55vh; margin:0; padding:0; overflow:auto; list-style:none; }
.columns-config-list li { display:grid; grid-template-columns:24px minmax(0,1fr) 28px 30px 30px; align-items:center; gap:5px; min-height:38px; padding:5px 7px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:5px; cursor:grab; }
.columns-config-list li.dragging { opacity:.45; background:#dbeafe; }.columns-config-list li.hidden{background:#f1f5f9}.columns-config-list li:active{cursor:grabbing}
.column-drag-handle { color:#94a3b8; text-align:center; }.column-visibility-control{display:flex;align-items:center;justify-content:center;width:28px;height:28px;margin:0;cursor:pointer}.column-visibility-control input{width:16px;height:16px;margin:0;accent-color:var(--color-navy);cursor:pointer}.column-config-name{overflow:hidden;color:#334155;font-size:.78rem;font-weight:600;text-overflow:ellipsis;white-space:nowrap}.columns-config-list li.hidden .column-config-name{color:#94a3b8;text-decoration:line-through}.sr-only{position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.columns-config-list button { display:flex; align-items:center; justify-content:center; width:28px; height:28px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; color:var(--color-navy); cursor:pointer; }
.columns-config-list button:disabled { opacity:.35; cursor:not-allowed; }
.columns-config-footer{display:flex;justify-content:flex-end;gap:8px;margin-top:12px}.reset-columns-btn,.apply-columns-btn{padding:7px 12px;border:1px solid #cbd5e1;border-radius:4px;cursor:pointer;font-size:.75rem;font-weight:600}.reset-columns-btn{background:#fff;color:var(--color-navy)}.apply-columns-btn{background:var(--color-navy);color:#fff}
.table-wrapper { position:relative; flex:1; min-height:0; width:100%; overflow:auto; }
.initial-loading { position:absolute; inset:0; z-index:20; display:flex; align-items:center; justify-content:center; gap:10px; background:#fff; color:#475569; }
.loading-spinner { width:22px; height:22px; border:3px solid #dbeafe; border-top-color:var(--color-navy); border-radius:50%; animation:spin .8s linear infinite; } @keyframes spin{to{transform:rotate(360deg)}}
.loading-spinner.small { width:14px; height:14px; border-width:2px; flex-shrink:0; }
.filter-progress-banner { display:flex; align-items:center; gap:8px; padding:6px 14px; background:#eef6fc; border-bottom:1px solid #cfe4f5; color:#1e3a5f; font-size:.78rem; }
.filter-progress-message { flex:1; }
.cancel-filter-btn { padding:4px 10px; border:1px solid #94a3b8; border-radius:4px; background:#fff; color:#334155; font-size:.75rem; font-weight:600; cursor:pointer; }
.cancel-filter-btn:hover { background:#f1f5f9; }
.attr-table { min-width:100%; table-layout:fixed; border-collapse:collapse; }
.attr-table col { width:250px; }.col-header,.data-cell{width:250px;min-width:250px;max-width:250px}
.col-header { position:sticky; top:0; z-index:2; overflow:hidden; padding:11px 10px; background:#f8fafc; border-right:1px solid #f1f5f9; border-bottom:2px solid #e2e8f0; color:#1e293b; cursor:grab; font-size:.75rem; text-overflow:ellipsis; text-transform:uppercase; white-space:nowrap; user-select:none; }
.col-header:active { cursor:grabbing; }.col-header.dragging { opacity:.45; background:#dbeafe; }
.filter-cell { position:sticky; top:39px; z-index:2; padding:6px; background:#fff; border-bottom:1px solid #e2e8f0; vertical-align:top; }
.attachment-filter-placeholder { color:#94a3b8; display:block; font-size:.75rem; padding:8px 4px; text-align:center; }
.extent-filter-icon { margin-top:4px; margin-left:4px; font-size:.5em; color:#fff; }
.data-cell { overflow:hidden; padding:10px 12px; border-right:1px solid #f8fafc; border-bottom:1px solid #f1f5f9; color:#334155; font-size:.84rem; text-overflow:ellipsis; white-space:nowrap; }
.data-cell--attachment { text-align:center; }
.clickable-row { cursor:pointer; }.clickable-row:hover{background:#eef6fc}.clickable-row.odd{background:#f8fafc}.clickable-row.selected{background:#fff0d8}.no-data{padding:30px;color:#64748b;text-align:center}
@media(max-width:600px){.header-bar{grid-template-columns:1fr;padding:10px 12px}.layer-title,.record-summary{text-align:center;justify-self:stretch}.header-tools{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;justify-self:stretch}.icon-btn{width:100%;min-width:0}.columns-config-modal{max-height:90vh;padding:12px}.columns-config-list{max-height:62vh}}
</style>

<style>
/* Sin scope a propósito: el diálogo genérico de vue-js-modal (<v-dialog/>,
   App.vue) se monta en su propio contenedor dinámico fuera del árbol de
   este componente, así que un <style scoped> de acá nunca le llegaría
   (mismo patrón que UploadShpKmz.vue). */
.attributes-table-confirm-button {
  color: var(--color-navy, #0e2a40);
  font-weight: 700;
}
</style>
